import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import HorizonRing from "@/components/HorizonRing";
import { BRANCH_META } from "@/lib/branches";
import { ArrowLeft, Users, Eye, Activity } from "lucide-react";

export default function Horizon() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/horizon/${slug}`).then((r) => setData(r.data));
    }, [slug]);

    if (!data) return null;
    const meta = BRANCH_META[data.discipline.branch];

    return (
        <div className="min-h-screen pt-24 pb-16 px-4 sm:px-8">
            <TopBar />
            <div className="max-w-6xl mx-auto">
                <Link
                    to={`/discipline/${slug}`}
                    className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
                    data-testid="back-to-discipline"
                >
                    <ArrowLeft className="w-4 h-4" /> {data.discipline.name} hub
                </Link>

                <div className="grid md:grid-cols-3 gap-6 mt-4">
                    <div className="md:col-span-2 crystal rounded-3xl p-8">
                        <span className="chip" style={{ background: meta.soft, color: meta.text }}>
                            {meta.name} · Horizon Chart
                        </span>
                        <h1
                            className="font-display font-black text-5xl tracking-tighter text-slate-900 mt-3 leading-none"
                            data-testid="horizon-title"
                        >
                            {data.discipline.name}
                        </h1>
                        <p className="text-slate-600 mt-2">
                            Fog of war lifting slowly. Here's what the community has already lit — and what remains.
                        </p>

                        <div className="mt-8 space-y-4">
                            {data.goals.map((g, i) => (
                                <div key={i}>
                                    <div className="flex items-center justify-between">
                                        <span
                                            className={`font-display font-bold ${g.done ? "text-slate-900" : "text-slate-700"}`}
                                        >
                                            {g.name}
                                        </span>
                                        <span className="text-xs font-mono text-slate-500">
                                            {g.pct}%
                                        </span>
                                    </div>
                                    <div className="mt-1.5 h-3 rounded-full overflow-hidden bg-slate-200/70">
                                        <div
                                            className="h-full rounded-full"
                                            style={{
                                                width: `${g.pct}%`,
                                                background: g.done
                                                    ? `linear-gradient(90deg, ${meta.color}, ${meta.color})`
                                                    : `repeating-linear-gradient(45deg, ${meta.color} 0 8px, ${meta.color}80 8px 16px)`,
                                                transition: "width 500ms",
                                            }}
                                            data-testid={`goal-bar-${i}`}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="crystal rounded-3xl p-6 text-center">
                            <HorizonRing size={140} stroke={14} pct={data.discipline.progress} color={meta.color} label="mapped" />
                            <p className="text-xs uppercase tracking-widest font-bold text-slate-400 mt-3">
                                Global progress
                            </p>
                        </div>
                        <div className="crystal rounded-3xl p-6 space-y-3">
                            <Stat icon={<Users className="w-4 h-4" />} label="Contributors" value={data.contributors} color={meta.color} />
                            <Stat icon={<Eye className="w-4 h-4" />} label="Observers" value={data.observers} color={meta.color} />
                            <Stat icon={<Activity className="w-4 h-4" />} label="Activity events" value={data.activity} color={meta.color} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Stat({ icon, label, value, color }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: color }}>
                {icon}
            </div>
            <div>
                <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">{label}</div>
                <div className="font-display font-black text-2xl text-slate-900 tracking-tighter">{value}</div>
            </div>
        </div>
    );
}
